import React, { lazy, Suspense, useState } from 'react';
import {
  InOut,
} from '../../model/model';

import { Form, Input, Button, Tabs, Select, Spin } from 'antd';

import { onFinishFailed } from '../helpers';

// Lazily load the ChirpStack config form so it is only bundled when needed.
const ChirpStackSettingsForm = lazy(
  () => import('../../plug/io/chirpstack/SettingsForm'),
);

// Lazily load the TTN config form so it is only bundled when needed.
const TtnSettingsForm = lazy(
  () => import('../../plug/io/ttn/SettingsForm'),
);

const IO_TYPES = ['CHIRPSTACK', 'TTN'] as const;
type IoType = typeof IO_TYPES[number];

interface IProps {
  initialValues: InOut;
  onFinish: (obj: InOut) => void;
  update?: boolean;
}

/**
 * Parses the `config` JSON string stored on an {@link InOut} into an object.
 * Returns an empty object on any parse error.
 */
function parseConfig(config: string | undefined | null): Record<string, unknown> {
  if (!config) return {};
  try {
    return JSON.parse(config);
  } catch {
    return {};
  }
}

function InOutForm(props: IProps) {
  const [selectedType, setSelectedType] = useState<string>(
    props.initialValues.type ?? '',
  );

  // Controlled config state – kept as a plain object so sub-forms can mutate it.
  const [configObj, setConfigObj] = useState<Record<string, unknown>>(
    parseConfig(props.initialValues.config),
  );

  const onFinish = (values: InOut) => {
    const v = Object.assign(props.initialValues, values);
    // Serialise the config object back to a JSON string before submitting.
    v.config = JSON.stringify(configObj);
    props.onFinish(v);
  };

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    // Reset config when the type changes so stale fields are not carried over.
    setConfigObj({});
  };

  return (
    <Form
      layout="vertical"
      initialValues={props.initialValues}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
    >
      <Tabs>
        <Tabs.TabPane tab="General" key="1">
          <Form.Item
            label="ID"
            name="id"
            rules={[{ required: true, message: 'Please enter an ID!' }]}
          >
            <Input.TextArea disabled={props.update} />
          </Form.Item>

          <Form.Item
            label="Type"
            name="type"
            rules={[{ required: true, message: 'Please select a type!' }]}
          >
            <Select
              placeholder="Select IO type"
              onChange={handleTypeChange}
              options={IO_TYPES.map((t) => ({ label: t, value: t }))}
            />
          </Form.Item>

          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item label="Description" name="description">
            <Input.TextArea />
          </Form.Item>
        </Tabs.TabPane>

        {selectedType === 'CHIRPSTACK' && (
          <Tabs.TabPane tab="ChirpStack Config" key="chirpstack">
            <Suspense fallback={<Spin />}>
              <ChirpStackSettingsForm
                value={configObj as any}
                onChange={(cfg) => setConfigObj(cfg as unknown as Record<string, unknown>)}
              />
            </Suspense>
          </Tabs.TabPane>
        )}

        {selectedType === 'TTN' && (
          <Tabs.TabPane tab="TTN Config" key="ttn">
            <Suspense fallback={<Spin />}>
              <TtnSettingsForm
                value={configObj as any}
                onChange={(cfg) => setConfigObj(cfg as unknown as Record<string, unknown>)}
              />
            </Suspense>
          </Tabs.TabPane>
        )}
      </Tabs>

      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

export default InOutForm;
